import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check, ChevronsLeftRight } from "lucide-react";
import { IMAGES } from "./data";

const EASE = [0.16, 1, 0.3, 1];

function BeforeAfterSlider() {
  const ref = useRef(null);
  const dragging = useRef(false);
  const [pos, setPos] = useState(50);

  const move = (clientX) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setPos(Math.min(94, Math.max(6, ((clientX - r.left) / r.width) * 100)));
  };

  useEffect(() => {
    const up = () => (dragging.current = false);
    const mv = (e) => dragging.current && move(e.clientX);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointermove", mv);
    return () => {
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointermove", mv);
    };
  }, []);

  return (
    <div
      ref={ref}
      data-testid="results-before-after-slider"
      onPointerDown={(e) => {
        dragging.current = true;
        move(e.clientX);
      }}
      className="relative aspect-[4/5] sm:aspect-[4/3] lg:aspect-[4/5] overflow-hidden rounded-[2rem] border border-[#E5DDD0] select-none cursor-ew-resize touch-none shadow-[0_40px_80px_-40px_rgba(44,38,35,0.4)]"
    >
      <img
        src={IMAGES.portrait1}
        alt="Skin after a course of Luviae treatments"
        className="absolute inset-0 w-full h-full object-cover"
        draggable="false"
      />
      <div
        className="absolute inset-0"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <img
          src={IMAGES.portrait1}
          alt="Skin before treatment"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: "grayscale(0.55) saturate(0.7) contrast(0.9) brightness(0.92)" }}
          draggable="false"
        />
      </div>

      <span className="absolute top-5 left-5 rounded-full bg-ink/70 backdrop-blur px-4 py-1.5 text-[11px] uppercase tracking-[0.2em] text-cream font-semibold">
        Before
      </span>
      <span className="absolute top-5 right-5 rounded-full bg-clay/85 backdrop-blur px-4 py-1.5 text-[11px] uppercase tracking-[0.2em] text-cream font-semibold">
        After
      </span>

      <div
        className="absolute top-0 bottom-0 w-px bg-cream/90"
        style={{ left: `${pos}%` }}
      >
        <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 rounded-full bg-cream border border-[#E5DDD0] shadow-xl flex items-center justify-center text-ink">
          <ChevronsLeftRight size={18} />
        </span>
      </div>
    </div>
  );
}

export default function Results() {
  return (
    <section id="results" className="py-24 lg:py-32 bg-[#F5F0E8]">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-14 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE }}
          className="lg:col-span-6"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-clay font-semibold mb-6">
            Authentic Clinical Results
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-snug text-ink mb-6">
            Real outcomes.
            <span className="italic text-clay"> Zero filters.</span>
          </h2>
          <p className="text-base text-ink/70 leading-relaxed mb-8 max-w-md">
            Drag the handle to see the kind of transformation our clients come
            back for. Every outcome study at your consultation is unretouched —
            we'd rather show you honest skin than sell you a fantasy.
          </p>
          <ul className="space-y-3 mb-8">
            {[
              "Hyperpigmentation & sun damage visibly faded",
              "Acne scarring smoothed over a course of sessions",
              "Vascular lesions and redness calmed",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm text-ink/70">
                <span className="mt-0.5 w-5 h-5 rounded-full bg-sage/15 flex items-center justify-center shrink-0">
                  <Check size={12} className="text-sage" />
                </span>
                {item}
              </li>
            ))}
          </ul>
          <p className="text-xs text-ink/40 leading-relaxed max-w-sm">
            Imagery on this page is illustrative of treatment outcomes.
            Genuine client before-and-after studies are shared during your
            consultation, with consent.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
          className="lg:col-span-6"
        >
          <BeforeAfterSlider />
        </motion.div>
      </div>
    </section>
  );
}
